import { useEffect, useRef, useState } from "react";
import { Filter, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export default function DateRangeFilter({
  from,
  to,
  onApply,
  onClear,
  align = "left",
  buttonClassName = "",
  maxPastMonths = 0,
  disableFuture = false,
  isLight = false,
  open: controlledOpen, // optional controlled prop
  onOpenChange,
}) {
  const [open, setOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(from || "");
  const [draftTo, setDraftTo] = useState(to || "");
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [activeInput, setActiveInput] = useState('from');
  const popRef = useRef(null);

  useEffect(() => {
    const onClickAway = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) {
        if (controlledOpen !== undefined) onOpenChange?.(false);
        else setOpen(false);
      }
    };
    const isOpen = controlledOpen !== undefined ? controlledOpen : open;
    if (isOpen) document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, [open, controlledOpen, onOpenChange]);

  useEffect(() => { setDraftFrom(from || ""); }, [from]);
  useEffect(() => { setDraftTo(to || ""); }, [to]);

  useEffect(() => {
    if (!open) return;
    const activeDate = parseYMD(activeInput === 'from' ? draftFrom : draftTo) || parseYMD(draftFrom) || parseYMD(draftTo) || new Date();
    setCalendarMonth(new Date(activeDate.getFullYear(), activeDate.getMonth(), 1));
  }, [open, activeInput, draftFrom, draftTo]);

  const parseYMD = (value) => {
    if (!value) return null;
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return null;
    const date = new Date(year, month - 1, day);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const formatYMD = (d) => {
    try {
      const date = d instanceof Date ? d : new Date(d);
      if (Number.isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (e) {
      return '';
    }
  };

  const formatDisplayDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const todayStr = formatYMD(new Date());
  const maxAllowed = disableFuture ? todayStr : "";
  let minAllowed = "";
  if (maxPastMonths && Number.isFinite(maxPastMonths) && maxPastMonths > 0) {
    const m = new Date();
    m.setMonth(m.getMonth() - Number(maxPastMonths));
    minAllowed = formatYMD(m);
  }

  const selectedFrom = parseYMD(draftFrom);
  const selectedTo = parseYMD(draftTo);
  const selectedFromIso = selectedFrom ? formatYMD(selectedFrom) : null;
  const selectedToIso = selectedTo ? formatYMD(selectedTo) : null;
  const invalidRange = draftFrom && draftTo && draftFrom > draftTo;

  const clampToAllowed = (value) => {
    if (!value) return false;
    if (minAllowed && value < minAllowed) return false;
    if (maxAllowed && value > maxAllowed) return false;
    return true;
  };

  const addMonths = (date, amount) => {
    const next = new Date(date.getTime());
    next.setMonth(next.getMonth() + amount);
    return next;
  };

  const buildCalendar = (month) => {
    const startOfMonth = new Date(month.getFullYear(), month.getMonth(), 1);
    const startWeekDay = startOfMonth.getDay();
    const firstDay = new Date(startOfMonth.getTime());
    firstDay.setDate(firstDay.getDate() - startWeekDay);

    const weeks = [];
    for (let week = 0; week < 6; week += 1) {
      const days = [];
      for (let day = 0; day < 7; day += 1) {
        const date = new Date(firstDay.getTime());
        date.setDate(firstDay.getDate() + week * 7 + day);
        const iso = formatYMD(date);
        const isCurrentMonth = date.getMonth() === month.getMonth();
        const isToday = iso === todayStr;
        const isSelectedFrom = selectedFromIso === iso;
        const isSelectedTo = selectedToIso === iso;
        const isInRange = selectedFromIso && selectedToIso && iso > selectedFromIso && iso < selectedToIso;
        const isAllowed = clampToAllowed(iso);
        days.push({ iso, day: date.getDate(), isCurrentMonth, isToday, isSelectedFrom, isSelectedTo, isInRange, isAllowed });
      }
      weeks.push(days);
    }
    return weeks;
  };

  const calendarWeeks = buildCalendar(calendarMonth);

  const onSelectDate = (iso) => {
    if (!clampToAllowed(iso)) return;
    if (activeInput === 'from') {
      setDraftFrom(iso);
      if (draftTo && iso > draftTo) {
        setDraftTo('');
      }
      setActiveInput('to');
      setCalendarMonth(new Date(iso).getMonth() !== calendarMonth.getMonth() ? new Date(iso) : calendarMonth);
    } else {
      setDraftTo(iso);
      if (draftFrom && iso < draftFrom) {
        setDraftFrom(iso);
      }
    }
  };

  const apply = () => {
    if (invalidRange) return;
    onApply?.({ from: draftFrom || null, to: draftTo || null });
    if (controlledOpen !== undefined) onOpenChange?.(false);
    else setOpen(false);
  };

  const clear = () => {
    setDraftFrom("");
    setDraftTo("");
    onClear?.();
    if (controlledOpen !== undefined) onOpenChange?.(false);
    else setOpen(false);
  };

  const openPopover = () => {
    if (controlledOpen !== undefined) onOpenChange?.(!controlledOpen);
    else setOpen((v) => !v);
  };

  const panelClasses = isLight ? 'bg-white border-neutral-200 text-neutral-900' : 'bg-[#0A0C10] border-white/20 text-neutral-200';
  const buttonClasses = isLight ? 'bg-white border-neutral-200 text-neutral-900 hover:bg-neutral-50' : 'bg-[#111827] border-white/10 text-neutral-200 hover:bg-white/5';

  return (
    <div className="relative inline-block" ref={popRef}>
      <button
        type="button"
        onClick={openPopover}
        className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition focus:outline-none focus:ring-0 ${isLight ? 'bg-white border-neutral-200 text-neutral-700 shadow-sm hover:bg-neutral-50' : 'bg-white/5 border-neutral-800 text-neutral-200 hover:bg-neutral-800'} ${buttonClassName}`}
      >
        <Filter size={14} className={isLight ? 'text-neutral-700' : 'text-neutral-200'} />
        <span>{from && to ? "Filtered by date range" : "Filter by date range"}</span>
        <ChevronDown size={14} className={`opacity-70 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`} />
      </button>

      {(controlledOpen !== undefined ? controlledOpen : open) && (
        <div
          className={`absolute z-50 mt-2 w-80 rounded-2xl border p-4 shadow-xl ${panelClasses} ${align === 'right' ? 'right-0' : 'left-0'}`}
          style={{ backdropFilter: isLight ? 'none' : 'blur(16px)' }}
        >
          <div className={`mb-3 text-sm font-medium ${isLight ? 'text-neutral-700' : 'text-neutral-300'}`}>Choose range</div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <button
              type="button"
              onClick={() => setActiveInput('from')}
              className={`rounded-2xl border px-3 py-2 text-left text-xs ${activeInput === 'from' ? 'border-white/30 bg-white/10' : 'border-white/10 bg-transparent'} ${isLight ? 'text-neutral-900' : 'text-neutral-200'}`}
            >
              <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500">From</div>
              <div className="mt-1 font-medium">{draftFrom ? formatDisplayDate(draftFrom) : 'Select start'}</div>
            </button>
            <button
              type="button"
              onClick={() => setActiveInput('to')}
              className={`rounded-2xl border px-3 py-2 text-left text-xs ${activeInput === 'to' ? 'border-white/30 bg-white/10' : 'border-white/10 bg-transparent'} ${isLight ? 'text-neutral-900' : 'text-neutral-200'}`}
            >
              <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500">To</div>
              <div className="mt-1 font-medium">{draftTo ? formatDisplayDate(draftTo) : 'Select end'}</div>
            </button>
          </div>

          <div className={`flex items-center justify-between rounded-2xl border px-3 py-2 mb-3 ${isLight ? 'bg-white' : 'bg-[#111827]'}`}>
            <button
              type="button"
              onClick={() => setCalendarMonth(addMonths(calendarMonth, -1))}
              className={`rounded-full p-1 ${buttonClasses}`}
              aria-label="Previous month"
            >
              <ChevronLeft size={16} />
            </button>
            <div className="text-sm font-medium">
              {calendarMonth.toLocaleString('default', { month: 'long' })} {calendarMonth.getFullYear()}
            </div>
            <button
              type="button"
              onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
              className={`rounded-full p-1 ${buttonClasses}`}
              aria-label="Next month"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-[11px] uppercase tracking-[0.18em] text-neutral-500 mb-2">
            {WEEKDAY_LABELS.map((day) => (
              <div key={day} className="text-center">{day}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {calendarWeeks.map((week, weekIndex) =>
              week.map((day) => {
                const selected = day.isSelectedFrom || day.isSelectedTo;
                const activeRange = day.isInRange;
                return (
                  <button
                    key={`${weekIndex}-${day.iso}`}
                    type="button"
                    onClick={() => onSelectDate(day.iso)}
                    disabled={!day.isAllowed}
                    className={`min-h-[36px] rounded-2xl text-sm leading-none ${day.isCurrentMonth ? '' : 'opacity-40'} ${day.isAllowed ? 'cursor-pointer' : 'cursor-not-allowed opacity-20'} ${selected ? 'bg-white text-neutral-900' : activeRange ? 'bg-white/10 text-white' : isLight ? 'bg-white/5 text-neutral-900 hover:bg-neutral-100' : 'bg-transparent text-neutral-200 hover:bg-white/10'} ${day.isToday ? 'ring-1 ring-white/20' : ''}`}
                  >
                    {day.day}
                  </button>
                );
              })
            )}
          </div>

          {invalidRange && (
            <div className="mt-3 rounded-xl bg-rose-500/10 px-3 py-2 text-xs text-rose-200">End date must be after start date.</div>
          )}

          <div className="mt-4 flex items-center justify-between">
            <button
              type="button"
              onClick={clear}
              className={`text-xs underline underline-offset-2 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}
            >
              Clear
            </button>
            <button
              type="button"
              onClick={apply}
              disabled={invalidRange}
              className={`text-xs font-bold px-4 py-2 rounded-lg ${invalidRange ? 'opacity-50 cursor-not-allowed' : ''} ${isLight ? 'bg-neutral-900 text-white' : 'bg-white text-neutral-900'}`}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
